import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Bell, BellOff, Loader2, Trash2, ArrowRight, Search } from "lucide-react";
import {
  homesSavedListings, homesSavedSearches, homesDeleteSearch, homesMarkSearchSeen, homesToggleSearchAlerts,
} from "@/lib/api";
import { PropertyCard } from "@/components/homes/PropertyCard";
import { useSaved } from "@/context/SavedContext";
import { login } from "@/context/AuthContext";

export default function HomesSaved() {
  const saved = useSaved();
  const nav = useNavigate();
  const [tab, setTab] = useState("homes");
  const [listings, setListings] = useState(null);
  const [searches, setSearches] = useState(null);

  const loadListings = () => homesSavedListings().then((d) => setListings(d.listings)).catch(() => setListings([]));
  const loadSearches = () => homesSavedSearches().then((d) => setSearches(d.searches)).catch(() => setSearches([]));

  useEffect(() => { if (saved?.user) loadSearches(); }, [saved?.user]); // searches: once per login
  useEffect(() => { if (saved?.user) loadListings(); }, [saved?.user, saved?.savedIds]); // homes: on heart toggle

  if (saved && !saved.user) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 text-center" data-testid="homes-saved-signin">
        <Heart className="h-10 w-10 text-[#2563EB]" />
        <h1 className="mt-4 text-2xl font-black text-[#0F172A]">Save your favorite homes</h1>
        <p className="mt-2 text-slate-600">Sign in to save listings, store searches and get alerted when new matches appear.</p>
        <button onClick={() => login("/homes/saved")} className="mt-5 rounded-xl bg-[#2563EB] px-6 py-3 font-bold text-white hover:bg-[#1D4ED8]" data-testid="homes-saved-signin-btn">Sign in</button>
      </div>
    );
  }

  const removeSearch = async (id) => { await homesDeleteSearch(id); setSearches((s) => s.filter((x) => x.id !== id)); saved.reload(); toast("Search removed"); };
  const viewSearch = async (s) => {
    await homesMarkSearchSeen(s.id); saved.reload();
    const p = new URLSearchParams(s.params); nav(`/homes/search?${p.toString()}`);
  };
  const toggleAlerts = async (s) => {
    const next = !(s.alerts_enabled !== false);
    setSearches((arr) => arr.map((x) => (x.id === s.id ? { ...x, alerts_enabled: next } : x)));
    try {
      await homesToggleSearchAlerts(s.id, next);
      toast.success(next ? "Email alerts on — we'll notify you of new matches" : "Email alerts off");
    } catch {
      setSearches((arr) => arr.map((x) => (x.id === s.id ? { ...x, alerts_enabled: !next } : x)));
      toast.error("Couldn't update alerts");
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-10" data-testid="homes-saved">
      <h1 className="text-4xl font-black text-[#0F172A]">Your HomesPulse</h1>
      <div className="mt-5 flex gap-1 border-b border-slate-200">
        {[["homes", `Saved Homes`], ["searches", `Saved Searches`]].map(([v, label]) => (
          <button key={v} onClick={() => setTab(v)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-bold transition-colors ${tab === v ? "border-[#2563EB] text-[#2563EB]" : "border-transparent text-slate-500 hover:text-[#0F172A]"}`}
            data-testid={`saved-tab-${v}`}>{label}</button>
        ))}
      </div>

      {tab === "homes" && (
        <div className="mt-6" data-testid="saved-homes-tab">
          {listings === null ? <Center><Loader2 className="h-7 w-7 animate-spin text-[#2563EB]" /></Center>
            : listings.length === 0 ? (
              <Empty icon={Heart} title="No saved homes yet" body="Tap the heart on any listing to save it here." cta={["Browse homes", "/homes/search"]} />
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="saved-homes-grid">
                {listings.map((l) => <PropertyCard key={l.id} listing={l} />)}
              </div>
            )}
        </div>
      )}

      {tab === "searches" && (
        <div className="mt-6" data-testid="saved-searches-tab">
          {searches === null ? <Center><Loader2 className="h-7 w-7 animate-spin text-[#2563EB]" /></Center>
            : searches.length === 0 ? (
              <Empty icon={Bell} title="No saved searches" body="Save a search to get alerted when new matching homes are listed." cta={["Start a search", "/homes/search"]} />
            ) : (
              <div className="space-y-3" data-testid="saved-searches-list">
                {searches.map((s) => (
                  <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`saved-search-${s.id}`}>
                    <div>
                      <div className="flex items-center gap-2 font-bold text-[#0F172A]">
                        {s.name}
                        {s.new_matches > 0 && <span className="rounded-full bg-[#2563EB] px-2 py-0.5 text-[11px] font-bold text-white" data-testid={`saved-search-new-${s.id}`}>{s.new_matches} new</span>}
                      </div>
                      <div className="text-sm text-slate-500">{s.total_matches} homes match this search</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleAlerts(s)} title={s.alerts_enabled !== false ? "Email alerts on" : "Email alerts off"}
                        className={`rounded-full border p-2 transition-colors ${s.alerts_enabled !== false ? "border-[#2563EB] bg-[#EEF2FF] text-[#2563EB]" : "border-slate-300 text-slate-400 hover:bg-slate-50"}`}
                        data-testid={`toggle-alerts-${s.id}`}>
                        {s.alerts_enabled !== false ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                      </button>
                      <button onClick={() => viewSearch(s)} className="inline-flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1D4ED8]" data-testid={`view-search-${s.id}`}>View <ArrowRight className="h-4 w-4" /></button>
                      <button onClick={() => removeSearch(s.id)} className="rounded-full border border-slate-300 p-2 text-slate-500 hover:bg-slate-50" data-testid={`delete-search-${s.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

const Center = ({ children }) => <div className="flex h-48 items-center justify-center">{children}</div>;
function Empty({ icon: Icon, title, body, cta }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center">
      <Icon className="mx-auto h-9 w-9 text-slate-300" />
      <h3 className="mt-3 text-lg font-bold text-[#0F172A]">{title}</h3>
      <p className="mt-1 text-slate-500">{body}</p>
      <Link to={cta[1]} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#1D4ED8]"><Search className="h-4 w-4" /> {cta[0]}</Link>
    </div>
  );
}
